const pad = (value: number): string => String(value).padStart(2, '0');

const toDate = (value: string | Date): Date => (value instanceof Date ? value : new Date(value));

export const formatNumber = (value: number): string => value.toLocaleString('ru-RU');

export const formatMoney = (value: number): string =>
    value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const formatDate = (value: string | Date): string => {
    const date = toDate(value);
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

export const formatTime = (value: string | Date): string => {
    const date = toDate(value);
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const formatDateTime = (value: string | Date): string =>
    `${formatDate(value)} · ${formatTime(value)}`;

export const shortName = (fullName: string): string => {
    const [last, first, middle] = fullName.trim().split(/\s+/);
    if (!last) return '';
    if (!first) return last;

    const rest = [first, middle]
        .filter(Boolean)
        .map((part) => `${part[0].toUpperCase()}.`)
        .join(' ');

    return `${last} ${rest}`;
};

export const toLocalIso = (value: Date = new Date()): string =>
    `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}` +
    `T${pad(value.getHours())}:${pad(value.getMinutes())}`;

export const formatProjectId = (id: string): string =>
    id ? `№ ${id.slice(-6).toUpperCase()}` : '';


export function formatRangeLabel(page: number, perPage: number, total: number): string {
    if (!total) return '0 из 0';

    const from = (page - 1) * perPage + 1;
    const to = Math.min(page * perPage, total);

    return `${formatNumber(from)}–${formatNumber(to)} из ${formatNumber(total)}`;
}

export function plural(count: number, one: string, few: string, many: string): string {
    const n = Math.abs(count) % 100;
    const n1 = n % 10;
    if (n > 10 && n < 20) return many;
    if (n1 > 1 && n1 < 5) return few;
    if (n1 === 1) return one;
    return many;
}

export function formatRub(value: number): string {
    return `${formatMoney(value)} ₽`;
}

export function pluralWithCount(count: number, one: string, few: string, many: string): string {
    return `${count} ${plural(count, one, few, many)}`;
}

export const shortId = (id: string) => id.slice(-6).toUpperCase();

export function daysLabel(n: number): string {
    return n + ' ' + plural(n, 'день', 'дня', 'дней');
}

export const now = (): Date => new Date(2026, 6, 30, 14, 30, 0);

export const daysSince = (d: Date, from: Date = now()): number =>
    Math.floor((from.getTime() - d.getTime()) / 86400000);

export function agoLabel(d: Date): string {
    const n = daysSince(d);
    if (n <= 0) return 'сегодня';
    if (n === 1) return 'вчера';
    return daysLabel(n) + ' назад';
}

export async function copyText(text: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
}

export const round2 = (value: number): number => Math.round(value * 100) / 100;